import { type ReactNode, useEffect, useRef } from "react";
import { I18nextProvider } from "react-i18next";

import { setRequestLanguage } from "@mountayaapp/api";
import { createI18n, SUPPORTED_LANGUAGES, type SupportedLanguage } from "@mountayaapp/locales";
import { useIdentityStore } from "@mountayaapp/theme";

/**
 * clampLanguage narrows the store's loose preferences.language (a plain
 * z.string()) to a supported UI language, falling back to "en" when the value
 * is missing or unsupported. This keeps i18next from being initialized with a
 * language that has no resource bundle.
 */
const clampLanguage = (language: string | undefined): SupportedLanguage =>
  SUPPORTED_LANGUAGES.includes(language as SupportedLanguage)
    ? (language as SupportedLanguage)
    : "en";

/**
 * LocaleProviderProps wrap the subtree that consumes translations.
 */
interface LocaleProviderProps {
  children: ReactNode;
}

/**
 * LocaleProvider binds a single i18next instance to the React tree. It reads the
 * signed-in user's preferred language from the identity store and builds the
 * instance once (useRef) with that language resolved, so the very first paint is
 * already localized. A later language change (the store mutating after sign-in
 * or a preferences update) is synced by an effect calling changeLanguage —
 * syncing an external mutable store is a legitimate effect, not derived state.
 * It lives in the web app rather than @mountayaapp/theme because the docs app is
 * English-only and wires no locale provider.
 *
 * It is also the single place that tells the REST client which language to
 * announce. The backend resolves its own validation sentences off Accept-Language,
 * and the app splices those sentences into copy it has already translated — so the
 * two must agree on one language, and this is where that language is decided.
 * Setting it during render rather than in an effect means the very first request a
 * child fires already carries it, matching the first-paint-is-localized posture
 * above; the api package is a leaf that cannot read i18next, so it is pushed in.
 */
export const LocaleProvider = ({ children }: LocaleProviderProps) => {
  const language = useIdentityStore((state) => state.preferences?.language);
  const resolved = clampLanguage(language);

  const instanceRef = useRef<ReturnType<typeof createI18n> | null>(null);
  if (instanceRef.current === null) {
    instanceRef.current = createI18n(resolved);
  }

  const instance = instanceRef.current;

  setRequestLanguage(resolved);

  useEffect(() => {
    if (instance.language !== resolved) {
      void instance.changeLanguage(resolved);
    }
  }, [instance, resolved]);

  return <I18nextProvider i18n={instance}>{children}</I18nextProvider>;
};
